import {
  AOFL_PRODUCT_UUIDS,
  events,
  getBaseURL,
  LOCAL_STORAGE_KEYS,
  LOG_LEVEL,
  QUERY_PARAM_KEYS,
} from "./env";
import * as _ from "./util";

const API_ENDPOINT = "/Event/Log/init";
const API_PATH = "/ws/msl/0.2/json";
const API_AMSL_PATH = "/ws/amsl/0.1/json";
const API_PATH_VALIDATE = "/User/ValidateLogin/init";
const API_PATH_CAMPAIGN = "/Event/ThirdPartyConversion/init";
const API_PATH_EMAIL_CLICK = "/Recipient/LogEmailClick/";

interface EventConfig {
  eventType: string;
  eventData?: Record<string, unknown>;
}

function fetchConfig({ eventType, eventData }: EventConfig): RequestInit {
  switch (eventType) {
    case events.EV_PAGE_LOADED:
      return _.formatRequest({
        args: [
          [
            {
              url: eventType,
              kvp: {
                page_url: window.location.href,
                referrer: document?.referrer ? document.referrer : "",
              },
              page_info_key: "seo",
              time: Math.round(Date.now() / 1000),
            },
          ],
        ],
      });
    case events.EV_CAMPAIGN_ATTRIBUTION:
      return _.formatRequest({
        args: [
          {
            eventType: "landing",
            eventSource: "abcmouse_seo_attribute",
            eventId: eventData?.pageName,
          },
        ],
      });
    case events.EV_CAMPAIGN_ATTRIBUTION_LOG:
      return _.formatRequest({
        args: [eventData],
      });
    case events.EV_EMAIL_LINK_CLICKED:
      return _.formatAmslRequest({
        args: {
          aofl_product_uuid: AOFL_PRODUCT_UUIDS.ABCMOUSE,
          request_url: window.location.href,
        },
      });
    default:
      return _.formatRequest({
        args: [
          [
            {
              url: eventType,
              kvp: {
                linkName: eventData?.linkId,
                pageName: eventData?.pageName,
              },
              time: Math.round(Date.now() / 1000),
            },
          ],
        ],
      });
  }
}

function fireCampaignAttribution(
  event: RequestInit,
  onSuccess: (response: Response) => void,
  onFailure: (err: unknown) => void,
  onCompletion: () => void,
): Promise<void> {
  return fetch(`${getBaseURL()}${API_PATH}${API_PATH_CAMPAIGN}`, {
    ...event,
    credentials: "include",
  })
    .then(onSuccess)
    .catch(onFailure)
    .finally(onCompletion);
}

function fireEvent(
  event: RequestInit,
  onSuccess: (response: Response) => void,
  onFailure: (err: unknown) => void,
  onCompletion: () => void,
): Promise<void> {
  return fetch(`${getBaseURL()}${API_PATH}${API_ENDPOINT}`, {
    ...event,
    credentials: "include",
  })
    .then(onSuccess)
    .catch(onFailure)
    .finally(onCompletion);
}

function fireLoginCheck(
  onSuccess: (data: { payload?: { token_valid?: boolean } }) => void,
  onFailure: (err: unknown) => void,
  onCompletion: () => void,
): Promise<void> {
  return fetch(`${getBaseURL()}${API_PATH}${API_PATH_VALIDATE}`, {
    credentials: "include",
  })
    .then((response) => response.json())
    .then(onSuccess)
    .catch(onFailure)
    .finally(onCompletion);
}

function fireEmailLinkClick(
  onSuccess: (token: string) => void,
  onFailure: (err: unknown) => void,
  onCompletion: () => void,
): Promise<string | void> {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const emailClickParam = urlSearchParams.get(QUERY_PARAM_KEYS.EMAIL_CLICK);

  if (!emailClickParam || typeof emailClickParam !== "string") {
    onSuccess("");
    onCompletion();
    return Promise.resolve("");
  }

  return fetch(
    `${getBaseURL()}${API_AMSL_PATH}${API_PATH_EMAIL_CLICK}`,
    fetchConfig({ eventType: events.EV_EMAIL_LINK_CLICKED }),
  )
    .then((response) => response.headers.get("Aofl-Token") ?? "")
    .then(onSuccess)
    .catch(onFailure)
    .finally(onCompletion);
}

function routeEvent(eventType: string): (e?: Event) => Promise<void> {
  switch (eventType) {
    case events.EV_PAGE_LOADED:
      return () =>
        new Promise((resolve, reject) => {
          fireEvent(
            fetchConfig({ eventType }),
            () => {
              _.log(LOG_LEVEL.I);
              resolve();
            },
            (err) => {
              _.log(LOG_LEVEL.E);
              reject(err);
            },
            _.log(LOG_LEVEL.I),
          );
        });
    case events.EV_ELEMENT_CLICKED:
      return (e?: Event) =>
        new Promise((resolve, reject) => {
          if (!e) return resolve();
          getElementClickId(e)
            .then((linkId) => {
              fireEvent(
                fetchConfig({
                  eventType,
                  eventData: {
                    linkId,
                    pageName: _.createPageName(),
                  },
                }),
                () => {
                  _.log(LOG_LEVEL.I);
                  resolve();
                },
                (err) => {
                  _.log(LOG_LEVEL.E);
                  reject(err);
                },
                _.log(LOG_LEVEL.I),
              );
            })
            .catch(() => {
              _.log(LOG_LEVEL.E);
            });
        });
    case events.EV_VALIDATE_LOGIN:
      return () =>
        new Promise((resolve, reject) => {
          fireLoginCheck(
            (parsedData) => {
              if (parsedData?.payload?.token_valid) {
                _.publishAuthEvent(parsedData.payload.token_valid);
              } else {
                _.publishAuthEvent(false);
              }
              resolve();
            },
            (err) => {
              _.log(LOG_LEVEL.E);
              reject(err);
            },
            _.log(LOG_LEVEL.I),
          );
        });
    case events.EV_CAMPAIGN_ATTRIBUTION:
      return () =>
        new Promise((resolve, reject) => {
          const sourceTag = _.getSourceTag();
          const pageHash = _.md5(window.location.pathname);
          const cvalue = sourceTag ? sourceTag : pageHash;
          const pageName = window.location.pathname;

          fireCampaignAttribution(
            fetchConfig({ eventType, eventData: { pageName } }),
            () => {
              logCampaignEvent().then(resolve).catch(reject);
            },
            (err) => {
              _.log(LOG_LEVEL.E);
              reject(err);
            },
            _.log(LOG_LEVEL.I),
          );

          function logCampaignEvent(): Promise<void> {
            return new Promise((resolve, reject) => {
              fireEvent(
                fetchConfig({
                  eventType: events.EV_CAMPAIGN_ATTRIBUTION_LOG,
                  eventData: [
                    {
                      kvp: {
                        dimid: 3,
                        tag: cvalue,
                      },
                      url: "@dimChange",
                    },
                    {
                      url: "@campaignRefer",
                      kvp: {
                        s_cid: cvalue,
                        sub_affid: "",
                      },
                    },
                  ] as unknown as Record<string, unknown>,
                }),
                () => {
                  _.log(LOG_LEVEL.I);
                  resolve();
                },
                (err) => {
                  _.log(LOG_LEVEL.E);
                  reject(err);
                },
                _.log(LOG_LEVEL.I),
              );
            });
          }
        });
    case events.EV_EMAIL_LINK_CLICKED:
      return () =>
        new Promise((resolve, reject) => {
          fireEmailLinkClick(
            (aoflToken) => {
              if (aoflToken !== "") {
                window.localStorage.setItem(LOCAL_STORAGE_KEYS.AOFL_TOKEN, aoflToken);
              }
              _.log(LOG_LEVEL.I);
              resolve();
            },
            (err) => {
              _.log(LOG_LEVEL.E);
              reject(err);
            },
            _.log(LOG_LEVEL.I),
          );
        });
    default:
      return () => {
        _.log(LOG_LEVEL.E);
        return Promise.resolve();
      };
  }
}

async function getElementClickId(e: Event): Promise<string> {
  const target = e.target as Element;
  const closestA = target.closest("a");
  if (!closestA) return "";
  const linkElementId = closestA.getAttribute("id");
  return linkElementId ? linkElementId : await getElementClickIdFromPath(closestA);
}

async function getElementClickIdFromPath(element: Element): Promise<string> {
  const jsPath = _.getJSPath(element);
  const hashedElementPath = await _.hashElementPath(jsPath);
  return hashedElementPath.slice(0, 8);
}

function addEventLoggerForClicks(): void {
  const links = document.getElementsByTagName("a");
  for (const eachA of Array.from(links)) {
    eachA.addEventListener("click", routeEvent(events.EV_ELEMENT_CLICKED));
  }
}

function registerEvents(e?: Event): void {
  if (window.location.pathname.includes("wp-admin")) {
    return;
  }

  const sourceTag = _.getSourceTag();
  const pageHash = _.md5(window.location.pathname);
  const cvalue = sourceTag ? sourceTag : pageHash;
  _.publishCampaignEvent(cvalue);
  addEventLoggerForClicks();

  routeEvent(events.EV_VALIDATE_LOGIN)(e)
    .then(() => routeEvent(events.EV_VALIDATE_LOGIN)(e))
    .then(() => routeEvent(events.EV_EMAIL_LINK_CLICKED)(e))
    .then(() => routeEvent(events.EV_CAMPAIGN_ATTRIBUTION)(e))
    .then(() => routeEvent(events.EV_PAGE_LOADED)(e))
    .catch((err) => _.log(LOG_LEVEL.E)(err));
}

export default registerEvents;
